/**
 * Role definitions synchronized with Yii2 backend
 * These IDs must match the role_id values in the Yii2 database
 */

export const ROLES = {
  USER: 0,            // Частное лицо
  ADMIN: 1,           // Администратор системы
  DIRECTOR: 2,        // Директор
  ACCOUNTANT: 3,      // Бухгалтер
  MANAGER: 4,         // Старший менеджер
  SPECIALIST: 5,      // Специалист
  TOP_MANAGER: 6,     // Топ менеджер
  EMPLOYEE: 7,        // Сотрудник
  DEVELOPER: 8,       // Разработчик
  EDITOR: 9,          // Редактор
  TRAVEL_MANAGER: 10, // Трэвел менеджер
  ADMIN_ACCOUNTANT: 11, // Админ бухгалтер
  OPERATOR: 12,           // Оператор
  EMPLOYEE_TRAVELLER: 13, // Тревелер
  GUEST_TRAVELLER: 14,    // Guest traveller (login-less travel profile)
  TOP_ADMIN: 15,          // Company executives (CEO/COO) — ADMIN + management panels
  TMC_ADMIN: 16,          // Admin of a partner travel company, confined to its own storefront
} as const

export type RoleId = (typeof ROLES)[keyof typeof ROLES]
export type RoleName = keyof typeof ROLES

/**
 * User type definitions synchronized with Yii2 backend (UserType enum)
 */
export const USER_TYPES = {
  INDIVIDUAL: 0,
  TRAVEL_AGENCY: 1,
  PRIVATE_COMPANY: 2,
} as const

export type UserTypeId = (typeof USER_TYPES)[keyof typeof USER_TYPES]

/**
 * Check if user is agency or company (not individual)
 */
export function isAgencyOrCompany(userType?: number): boolean {
  return userType === USER_TYPES.TRAVEL_AGENCY || userType === USER_TYPES.PRIVATE_COMPANY
}

/**
 * Company admins - users who can see all company data,
 * approve expenses, manage cards, etc.
 */
export const COMPANY_ADMIN_ROLES: RoleId[] = [
  ROLES.ADMIN,
  ROLES.TOP_ADMIN,
  ROLES.DEVELOPER,
  ROLES.DIRECTOR,
  ROLES.TOP_MANAGER,
  ROLES.ACCOUNTANT,
  // A partner travel company's admin is one of these for his own company, which is what this
  // list means. Being here is what gives him the analytics a director has (cost differential,
  // cost savings, duty of care) — and, deliberately, the rest of the director's reach too:
  // per-diem rules, receipt review and moving the company's own money. Parity with the
  // director was the product decision (Andrey, 27.09.2026); it never crosses into another
  // company, because each of those surfaces scopes by the caller's owner tree.
  ROLES.TMC_ADMIN,
]

/**
 * Check if a role has company admin privileges
 */
export function isCompanyAdmin(roleId: RoleId | number): boolean {
  return COMPANY_ADMIN_ROLES.includes(roleId as RoleId)
}

/**
 * Who may switch the flight results between cards and the compact table.
 * COMPANY_ADMIN_ROLES plus ADMIN_ACCOUNTANT, which that list leaves out.
 */
export const FLIGHT_TABLE_VIEW_ROLES: RoleId[] = [
  ...COMPANY_ADMIN_ROLES,
  ROLES.ADMIN_ACCOUNTANT,
]

export function canUseFlightTableView(roleId: RoleId | number | undefined): boolean {
  return roleId != null && FLIGHT_TABLE_VIEW_ROLES.includes(roleId as RoleId)
}

/**
 * Who may create company budgets — mirrors the backend allow-set
 * (UserRole::getBudgetManagerRoles()). NOT the same as COMPANY_ADMIN_ROLES:
 * that list has no ADMIN_ACCOUNTANT, and the backend one does.
 */
export const BUDGET_MANAGER_ROLES: RoleId[] = [
  ROLES.ADMIN,
  ROLES.TOP_ADMIN,
  ROLES.DEVELOPER,
  ROLES.ADMIN_ACCOUNTANT,
  ROLES.DIRECTOR,
  ROLES.TOP_MANAGER,
  ROLES.ACCOUNTANT,
  // A partner's own admin, dividing their own company's money. Their scope is
  // their company on both sides — the owner they may name and the budgets they
  // may see — so this widens who creates, not what anybody sees.
  ROLES.TMC_ADMIN,
]

export function isBudgetManager(roleId: RoleId | number): boolean {
  return BUDGET_MANAGER_ROLES.includes(roleId as RoleId)
}

/**
 * Who may WRITE the company payment profile and the corporate cards that hang
 * off it — mirrors the one backend allow-set both share
 * (rest\models\PaymentProfile\BaseHandler and rest\models\CompanyCard\BaseHandler:
 * allowedRoles = [DIRECTOR, DEVELOPER], adminAllowed = false).
 *
 * Deliberately narrower than COMPANY_ADMIN_ROLES and NOT admin-bypassed: reading
 * is support-safe (the GET handlers opt into the bypass), writing is the
 * director's. A top-manager, accountant or platform admin counts as a company
 * admin elsewhere in the app but gets a 403 here — so anything offered on that
 * broader test alone is a control that can only fail, or a "you can change this"
 * promise to someone who cannot.
 */
export const PAYMENT_PROFILE_MANAGER_ROLES: RoleId[] = [
  ROLES.DIRECTOR,
  ROLES.DEVELOPER,
  ROLES.TMC_ADMIN,
]

export function isPaymentProfileManager(roleId: RoleId | number): boolean {
  return PAYMENT_PROFILE_MANAGER_ROLES.includes(roleId as RoleId)
}

/**
 * System/platform admins (not company admins like DIRECTOR)
 */
export const SYSTEM_ADMIN_ROLES: RoleId[] = [ROLES.ADMIN, ROLES.TOP_ADMIN, ROLES.DEVELOPER, ROLES.ADMIN_ACCOUNTANT, ROLES.OPERATOR]

export function isSystemAdmin(roleId: RoleId | number): boolean {
  return SYSTEM_ADMIN_ROLES.includes(roleId as RoleId)
}

/**
 * Exactly `UserRole::getSuperAdminRoles()` on the server.
 *
 * Deliberately not SYSTEM_ADMIN_ROLES, which also carries OPERATOR: an operator
 * passes `isSystemAdmin` and would be shown a menu entry that answers 403,
 * because the server-side list does not include them. Where a page is gated by
 * getSuperAdminRoles(), gate the menu by this.
 */
export const SUPER_ADMIN_ROLES: RoleId[] = [ROLES.ADMIN, ROLES.TOP_ADMIN, ROLES.DEVELOPER, ROLES.ADMIN_ACCOUNTANT]

export function isSuperAdmin(roleId: RoleId | number | undefined): boolean {
  return SUPER_ADMIN_ROLES.includes(roleId as RoleId)
}

// Mirrors UserRole::MAIN_ADMINS.
export const MAIN_ADMIN_ROLES: RoleId[] = [ROLES.ADMIN, ROLES.DEVELOPER, ROLES.TOP_ADMIN]

export function isMainAdmin(roleId: RoleId | number | undefined): boolean {
  return MAIN_ADMIN_ROLES.includes(roleId as RoleId)
}

/**
 * Everyone who works for us, whatever they do here.
 *
 * Mirrors the server's UserRole::getAdminRoles() exactly — deliberately wider
 * than SYSTEM_ADMIN_ROLES, which leaves out the two manager roles. It gates
 * things that are back-office by nature rather than by permission: a change
 * request about an order is asked by a manager and decided by an accountant,
 * and a client company has no business seeing either half.
 */
export const STAFF_ROLES: RoleId[] = [
  ROLES.ADMIN,
  ROLES.TOP_ADMIN,
  ROLES.MANAGER,
  ROLES.SPECIALIST,
  ROLES.DEVELOPER,
  ROLES.ADMIN_ACCOUNTANT,
  ROLES.OPERATOR,
]

export function isStaff(roleId: RoleId | number | undefined): boolean {
  return roleId !== undefined && STAFF_ROLES.includes(roleId as RoleId)
}

/**
 * Platform staff = the roles the backend refuses to impersonate (mirrors
 * UserRole::getAdminRoles()). X-On-Behalf-Of returns 403 for these, so features
 * that create data on their behalf (e.g. receipt-email forwarding) don't work
 * for them — used to hide such dead-end affordances.
 */
export const PLATFORM_STAFF_ROLES: RoleId[] = [
  ROLES.ADMIN,
  ROLES.TOP_ADMIN,
  ROLES.MANAGER,
  ROLES.SPECIALIST,
  ROLES.DEVELOPER,
  ROLES.ADMIN_ACCOUNTANT,
  ROLES.OPERATOR,
]

export function isPlatformStaff(roleId: RoleId | number): boolean {
  return PLATFORM_STAFF_ROLES.includes(roleId as RoleId)
}

/**
 * Who may use admin mode on the order page. Mirrors
 * common/services/Order/View/OrderAdminModeAccess — keep the two in step.
 *
 * Derived, not listed: "platform staff, minus those scoped to their own
 * agency". OPERATOR passes every staff gate and is still narrowed everywhere
 * else (OrderSearchAccessResolver::isStaffSearch), and admin mode exists to
 * show net, cost and partner payments — so the gate has to be the scope.
 * Writing today's roles out would diverge the moment that rule moves.
 *
 * All seven of our admin roles — the server's `$viewer->isAdmin`.
 *
 * OPERATOR is IN, which is a reversal: he used to be filtered out here on the
 * strength of the scope helpers, which exclude him from reading the whole
 * platform. Those answer a question about lists; this one is about one order
 * the reader already opened, and `Order::getHasAccess()` lets an OPERATOR open
 * only the orders he manages. Nothing on one of those is somebody else's.
 */
export const ORDER_ADMIN_ROLES: RoleId[] = PLATFORM_STAFF_ROLES

export function canUseOrderAdminMode(roleId: RoleId | number | undefined): boolean {
  // A partner travel company's admin, with no allowlist: admin mode is his
  // order card, not a preview of ours. Deliberately the bare role here where
  // the server asks IssuingCompanyIsolation::isPartnerAdmin() — the browser
  // knows the role but not the storefront, and this only decides whether the
  // view switch is offered. The payload's `admin` section is what the page
  // actually renders from, so a TMC_ADMIN outside the isolated storefront sees
  // the switch do nothing rather than see anything he may not.
  if ((roleId ?? -1) === ROLES.TMC_ADMIN) {
    return true
  }

  return ORDER_ADMIN_ROLES.includes((roleId ?? -1) as RoleId)
}

/**
 * The employee-traveller role: travels, but manages nothing and owns no data
 * beyond their own trips and their own traveller profile. Every page opened to
 * them is scoped to that by the backend.
 */
export function isTraveller(roleId: RoleId | number | undefined): boolean {
  return (roleId ?? -1) === ROLES.EMPLOYEE_TRAVELLER
}

/**
 * Roles with travel management permissions (employees, fares, policies)
 */
export const TRAVEL_MANAGEMENT_ROLES: RoleId[] = [ROLES.DIRECTOR, ROLES.TRAVEL_MANAGER]

export function isTravelManagement(roleId: RoleId | number): boolean {
  return TRAVEL_MANAGEMENT_ROLES.includes(roleId as RoleId)
}

/**
 * Whether the actor may manage (edit / manage sessions of) a target employee —
 * client mirror of the backend EmployeeRoleMatrixService::canManage: directors
 * and travel managers manage everyone in the company; anyone else — nobody.
 * Stricter per-action carve-outs (archive, email) live in their own helpers.
 * The backend enforces this too; this only hides UI a caller can't act on.
 */
export function canManageEmployee(
  actor: { roleId?: number; isAccountOwner?: boolean } | null | undefined,
  _targetRoleId: number,
): boolean {
  if (!actor || actor.roleId == null) return false
  return (
    isMainAdmin(actor.roleId) ||
    actor.roleId === ROLES.DIRECTOR ||
    actor.roleId === ROLES.TRAVEL_MANAGER ||
    actor.roleId === ROLES.TMC_ADMIN
  )
}

/**
 * Who may bulk-import employees from CSV — mirror of the backend
 * Users/Import allowedRoles: directors and staff only. A travel manager
 * manages employees one by one but never imports.
 */
export function canImportEmployees(roleId: RoleId | number | undefined): boolean {
  if (roleId == null) return false
  return roleId === ROLES.DIRECTOR || roleId === ROLES.TMC_ADMIN || isMainAdmin(roleId)
}

/**
 * Mirror of EmployeeRoleMatrixService::canChangeRole: a travel manager may
 * onboard travellers but never re-grade an existing employee; every other
 * manager role may. The backend enforces this — this only locks the select.
 */
export function canChangeEmployeeRole(
  actor: { roleId?: number } | null | undefined,
): boolean {
  if (!actor || actor.roleId == null) return false
  if (isMainAdmin(actor.roleId)) return true
  return actor.roleId !== ROLES.TRAVEL_MANAGER
}

/**
 * Mirror of EmployeeRoleMatrixService::canEditProfile: a travel manager may
 * touch a DIRECTOR only through travel policy — never their profile (name,
 * phone, expense). Other managers keep their rights (email has its own
 * carve-out). The backend enforces this; this only locks the inputs.
 */
export function canEditEmployeeProfile(
  actor: { roleId?: number } | null | undefined,
  targetRoleId: number,
): boolean {
  if (!actor || actor.roleId == null) return false
  if (isMainAdmin(actor.roleId)) return true
  return !(actor.roleId === ROLES.TRAVEL_MANAGER && targetRoleId === ROLES.DIRECTOR)
}

/**
 * Anti-takeover carve-out, mirror of EmployeeRoleMatrixService::canChangeEmail:
 * a director's email is changed only by the account owner or the director
 * himself; non-director targets follow canManageEmployee.
 */
export function canChangeEmployeeEmail(
  actor: { roleId?: number; isAccountOwner?: boolean } | null | undefined,
  targetRoleId: number,
  isSelf: boolean,
): boolean {
  if (!actor || actor.roleId == null) return false
  if (isMainAdmin(actor.roleId)) return true
  if (isSelf) return true
  if (targetRoleId !== ROLES.DIRECTOR) return canManageEmployee(actor, targetRoleId)
  return actor.roleId === ROLES.DIRECTOR && !!actor.isAccountOwner
}

/**
 * Roles that may create / edit / delete departments and their membership —
 * client mirror of the backend department write set (directors + staff). Travel
 * managers and accountants may VIEW the tree but not edit; the backend enforces
 * this too, this only hides UI they cannot act on.
 */
export const DEPARTMENT_MANAGE_ROLES: RoleId[] = [ROLES.DIRECTOR, ROLES.ADMIN, ROLES.TOP_ADMIN, ROLES.DEVELOPER, ROLES.TMC_ADMIN]

export function canManageDepartments(roleId: RoleId | number | undefined): boolean {
  if (roleId == null) return false
  return DEPARTMENT_MANAGE_ROLES.includes(roleId as RoleId)
}

/**
 * Who may create / edit / archive / import cost centers — client mirror of the backend
 * write set (DIRECTOR, ACCOUNTANT, MAIN_ADMINS, TMC_ADMIN, no admin bypass). The page
 * itself trusts `canManage` from the API; this only decides what the UI offers.
 */
export const COST_CENTER_MANAGE_ROLES: RoleId[] = [
  ROLES.DIRECTOR,
  ROLES.ACCOUNTANT,
  ROLES.ADMIN,
  ROLES.DEVELOPER,
  ROLES.TOP_ADMIN,
  ROLES.TMC_ADMIN,
]

export function canManageCostCenters(roleId: RoleId | number | undefined): boolean {
  if (roleId == null) return false
  return COST_CENTER_MANAGE_ROLES.includes(roleId as RoleId)
}

/**
 * Get role name by ID (for display purposes)
 */
export function getRoleName(roleId: RoleId | number): string {
  const entry = Object.entries(ROLES).find(([, id]) => id === roleId)
  return entry ? entry[0] : 'Unknown'
}

/**
 * Access rights matrix for Cost Management module
 */
export const COST_MANAGEMENT_ACCESS = {
  expenses: {
    viewAll: COMPANY_ADMIN_ROLES,      // Can see all company expenses
    approve: COMPANY_ADMIN_ROLES,       // Can approve/reject expenses
    create: 'all',                      // Everyone can create expenses
    viewOwn: 'all',                     // Everyone can see own expenses
  },
  wallet: {
    view: COMPANY_ADMIN_ROLES,
    manage: COMPANY_ADMIN_ROLES,
  },
  budgets: {
    view: COMPANY_ADMIN_ROLES,
    manage: COMPANY_ADMIN_ROLES,
  },
  cards: {
    viewAll: COMPANY_ADMIN_ROLES,       // Can see all company cards
    viewOwn: 'all',                     // Everyone can see own cards
    orderForOthers: COMPANY_ADMIN_ROLES, // Can order cards for other employees
    orderForSelf: 'all',                // Everyone can order card for themselves
  },
} as const

/**
 * Check if user has access to a specific feature
 */
export function hasAccess(
  roleId: RoleId | number,
  module: keyof typeof COST_MANAGEMENT_ACCESS,
  action: string
): boolean {
  const moduleAccess = COST_MANAGEMENT_ACCESS[module] as Record<string, RoleId[] | 'all'>
  const allowedRoles = moduleAccess[action]

  if (allowedRoles === 'all') return true
  if (Array.isArray(allowedRoles)) {
    return allowedRoles.includes(roleId as RoleId)
  }
  return false
}
